"use client";

import { useEffect, useState } from "react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import { classificationApi, type NdiAssessmentResult } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

// ─── Types ────────────────────────────────────────────────────────────────────

type StoredAssessment = NdiAssessmentResult & { _savedAt: string };

// ─── Icons ────────────────────────────────────────────────────────────────────

function IconClipboard() {
  return (
    <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden style={{ color: "#576E8A" }}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}
function IconLayers() {
  return (
    <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden style={{ color: "#576E8A" }}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}
function IconTrendUp() {
  return (
    <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden style={{ color: "#576E8A" }}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  );
}
function IconWarning() {
  return (
    <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden style={{ color: "#ef4444" }}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}
function IconDot({ color }: { color: string }) {
  return <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: color }} />;
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

interface KPICardProps {
  label: string; value: string; target?: string;
  icon: React.ReactNode; noTarget?: boolean; sub?: string;
}
function KPICard({ label, value, target, icon, noTarget, sub }: KPICardProps) {
  const { t, isAr } = useI18n();
  return (
    <div className="flex flex-col rounded-lg overflow-hidden"
      style={{ background: "var(--glass-bg)", backdropFilter: "blur(27px)", WebkitBackdropFilter: "blur(27px)", border: "1px solid var(--glass-border)", boxShadow: "var(--glass-shadow)", width: "254px", minHeight: "182px", padding: "24px", gap: "12px" }}>
      <div className="flex items-center justify-start" style={{ width: 48, height: 48 }}>{icon}</div>
      <span className="font-bold leading-tight" style={{ fontFamily: "var(--font-brand), serif", fontSize: "14px", color: "#576E8A" }}>{label}</span>
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="font-bold" style={{ fontFamily: "var(--font-brand), serif", fontSize: "30px", lineHeight: 1.37, letterSpacing: isAr ? "0" : "-0.027em", color: "#1E293B" }}>{value}</span>
          {sub && <p className="text-xs mt-0.5" style={{ color: "#9CA7B7" }}>{sub}</p>}
        </div>
        {!noTarget && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-sm font-normal shrink-0"
            style={{ background: "#DDF1E4", color: "#03C026", border: "0.5px solid #03C026", fontFamily: "var(--font-brand), serif" }}>
            {t("Target:", "الهدف:")} {target ?? "—"}
            <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden
              style={{ transform: isAr ? "scaleX(-1) translateY(-1px)" : "translateY(-1px)" }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 17L17 7M7 7h10v10" />
            </svg>
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────

function SectionCard({ title, children, className = "", variant = "chart", hideTitle = false }: {
  title: string; children: React.ReactNode; className?: string; variant?: "chart" | "table"; hideTitle?: boolean;
}) {
  const isChart = variant === "chart";
  return (
    <div className={`rounded-lg overflow-hidden border ${className}`} style={{ borderColor: "var(--card-border)", boxShadow: "var(--card-shadow)" }}>
      {!hideTitle && (
        <div className={`px-4 py-3 ${isChart ? "bg-white" : ""}`} style={!isChart ? { background: "var(--section-header-bg)" } : undefined}>
          <h3 className="font-semibold" style={{ fontSize: "22px", color: isChart ? "var(--text-heading)" : "var(--section-header-text)", fontFamily: "var(--font-brand), serif" }}>{title}</h3>
        </div>
      )}
      <div className="bg-white p-4">{children}</div>
    </div>
  );
}

// ─── Domain table row ─────────────────────────────────────────────────────────

function DomainRow({ domain, maturityScore, complianceScore, isLive, liveLabel = "Live" }: {
  domain: string; maturityScore: string; complianceScore: string; isLive?: boolean; liveLabel?: string;
}) {
  return (
    <tr className="border-b last:border-0" style={{ borderColor: "var(--card-border)" }}>
      <td className="px-4 py-3 text-sm" style={{ color: "var(--text-heading)" }}>
        <div className="flex items-center gap-2">
          {domain}
          {isLive && (
            <span className="text-xs px-1.5 py-0.5 rounded font-semibold" style={{ background: "#DDF1E4", color: "#03C026" }}>{liveLabel}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-sm" style={{ color: "var(--text-body)" }}>{maturityScore}</td>
      <td className="px-4 py-3 text-sm" style={{ color: "var(--text-body)" }}>{complianceScore}</td>
    </tr>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ORDERED_DOMAINS = [
  { en: "Data Governance", ar: "حوكمة البيانات" },
  { en: "Metadata and Data Catalog", ar: "البيانات الوصفية وفهرس البيانات" },
  { en: "Data Quality", ar: "جودة البيانات" },
  { en: "Data Storage", ar: "تخزين البيانات" },
  { en: "Content and Document Management", ar: "إدارة المحتوى والمستندات" },
  { en: "Data Modeling and Architecture", ar: "نمذجة وبنية البيانات" },
  { en: "Reference and Master Data Management", ar: "إدارة البيانات المرجعية والرئيسية" },
  { en: "Business Intelligence and Analytics", ar: "ذكاء الأعمال والتحليلات" },
  { en: "Data Integration and Sharing", ar: "تكامل ومشاركة البيانات" },
  { en: "Data Value Realization", ar: "تحقيق قيمة البيانات" },
  { en: "Open Data", ar: "البيانات المفتوحة" },
  { en: "Freedom of Information", ar: "حرية المعلومات" },
  { en: "Data Classification", ar: "تصنيف البيانات" },
  { en: "Personal Data Protection", ar: "حماية البيانات الشخصية" },
] as const;

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ExecutiveReadinessPage() {
  const { t, lang } = useI18n();
  const [assessment, setAssessment] = useState<StoredAssessment | null>(null);
  const [target, setTarget] = useState<{ maturityPercent: number } | null>(null);
  const [agentOnline, setAgentOnline] = useState<boolean | null>(null);

  // Read persisted NDI assessment from governance page
  useEffect(() => {
    // Try localStorage first (fast)
    try {
      const raw = localStorage.getItem("classification_ndi_result");
      if (raw) setAssessment(JSON.parse(raw));
      const targetRaw = localStorage.getItem("classification_target");
      if (targetRaw) setTarget(JSON.parse(targetRaw));
    } catch {
      // ignore stale or corrupt data
    }
    // Always sync from backend (source of truth)
    classificationApi.getMaturityAssessments().then((records) => {
      if (records.length === 0) return;
      const latest = records[0];
      setAssessment((prev) => {
        // Only overwrite localStorage state if backend has a newer record
        const prevSaved = prev?._savedAt ? new Date(prev._savedAt).getTime() : 0;
        const backendSaved = new Date(latest.assessed_at).getTime();
        if (backendSaved >= prevSaved) {
          return {
            ...(latest.raw_result as StoredAssessment),
            maturity_level: latest.maturity_level,
            maturity_label: latest.maturity_label,
            average_score: latest.score ?? undefined,
            _savedAt: latest.assessed_at,
          } as StoredAssessment;
        }
        return prev;
      });
      setTarget((prev) => {
        if (prev) return prev;
        return latest.target_level != null
          ? { maturityPercent: Math.round((latest.target_level / 5) * 100) }
          : null;
      });
    }).catch(() => {/* backend unavailable, localStorage already loaded */});
  }, []);

  // Fetch classification levels + agent health on mount
  useEffect(() => {
    classificationApi.getLevels()
      .then(() => { setAgentOnline(true); })
      .catch(() => setAgentOnline(false));
  }, []);

  // ── Derived values ──────────────────────────────────────────────────────────
  const maturityLevel = assessment?.maturity_level ?? null;
  const averageScore = assessment?.average_score ?? null;
  const dcCompliance = maturityLevel != null ? Math.round((maturityLevel / 5) * 100) : 0;
  const currentMaturityPercent = maturityLevel != null ? Math.round((maturityLevel / 5) * 100) : 0;
  const targetPercent = target?.maturityPercent ?? 100;

  const complianceRadarData = ORDERED_DOMAINS.map((domain) => ({
    domain: t(domain.en, domain.ar),
    current: domain.en === "Data Classification" ? dcCompliance : 0,
    target: domain.en === "Data Classification" ? targetPercent : 100,
  }));

  const maturityRadarData = ORDERED_DOMAINS.map((domain) => ({
    domain: t(domain.en, domain.ar),
    current: domain.en === "Data Classification" ? currentMaturityPercent : 0,
    target: domain.en === "Data Classification" ? targetPercent : 100,
  }));

  // Saved-at label
  const savedAt = assessment?._savedAt
    ? new Date(assessment._savedAt).toLocaleDateString(
        lang === "ar" ? "ar-SA" : "en-GB",
        { day: "numeric", month: "short", year: "numeric" }
      )
    : null;

  return (
    <div className="space-y-6" style={{ padding: "var(--page-padding)" }}>
      {/* ── Page header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="page-title">{t("Executive Readiness", "الجاهزية التنفيذية")}</h1>
          <p className="page-subtitle">{t("NDI Audit Cycle: Q1 2026 (Active)", "دورة تدقيق مؤشر البيانات: الربع الأول 2026 (نشطة)")}</p>
        </div>
        <div className="flex items-center gap-3">
          {agentOnline != null && (
            <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full"
              style={{ background: agentOnline ? "#DDF1E4" : "#fde8d8", color: agentOnline ? "#03C026" : "#c2410c" }}>
              <IconDot color={agentOnline ? "#03C026" : "#c2410c"} />
              {t("Classification Agent", "وكيل التصنيف")} {t(agentOnline ? "Online" : "Offline", agentOnline ? "متصل" : "غير متصل")}
            </span>
          )}
          <button type="button"
            className="shrink-0 px-5 py-2.5 rounded-xl text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-opacity hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #1a6fa8 0%, #163D6A 50%, #0d2a4a 100%)", boxShadow: "0 4px 14px rgba(18, 50, 87, 0.4)", fontFamily: "var(--font-brand), serif" }}>
            {t("Create Report", "إنشاء تقرير")}
          </button>
        </div>
      </div>

      {/* ── Assessment notice ── */}
      {!assessment ? (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm"
          style={{ background: "#fff3cd", border: "1px solid #ffc107", color: "#856404" }}>
          <IconWarning />
          <span>
            {t(
              "No assessment data yet. Go to ",
              "لا توجد بيانات تقييم حتى الآن. انتقل إلى "
            )}
            <strong>{t("Governance → Data Classification", "الحوكمة ← تصنيف البيانات")}</strong>
            {t(
              " and run the NDI Assessment to populate this dashboard.",
              " وقم بتشغيل تقييم مؤشر البيانات لملء لوحة المعلومات."
            )}
          </span>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm"
          style={{ background: "#DDF1E4", border: "1px solid #03C026", color: "#03C026" }}>
          <IconDot color="#03C026" />
          <span>
            {t("Data Classification assessed on ", "تم تقييم تصنيف البيانات في ")}
            <strong>{savedAt}</strong>
            {" — L"}{maturityLevel} {assessment.maturity_label?.split(" (")[0]}.
            {" "}{t("Remaining 13 domains are pending assessment.", "13 مجالاً متبقياً في انتظار التقييم.")}
          </span>
        </div>
      )}

      {/* ── KPI banner ── */}
      <div className="relative" style={{ paddingBottom: "120px" }}>
        <div className="rounded-t-xl" style={{ height: "160px", background: "linear-gradient(to bottom, #0d2a4a 0%, #163D6A 50%, #1a6fa8 100%)", boxShadow: "0 4px 24px rgba(13, 31, 64, 0.18)" }} />
        <div className="absolute left-0 right-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 px-6" style={{ top: "20px" }}>
          <KPICard
            label={t("Maturity Score", "درجة النضج")}
            value={maturityLevel != null ? `L${maturityLevel}` : "—"}
            target="L5"
            icon={<IconLayers />}
            sub={maturityLevel != null ? assessment?.maturity_label?.split(" (")[0] : t("Run assessment", "قم بالتقييم")}
          />
          <KPICard
            label={t("Compliance Score", "درجة الامتثال")}
            value={dcCompliance > 0 ? `${dcCompliance}%` : "—"}
            target="100%"
            icon={<IconClipboard />}
            sub={dcCompliance > 0 ? t("Data Classification", "تصنيف البيانات") : t("Run assessment", "قم بالتقييم")}
          />
          <KPICard
            label={t("Operation Excellence Score", "درجة التميز التشغيلي")}
            value={averageScore != null ? `${averageScore.toFixed(1)}/5` : "—"}
            target="5.0"
            icon={<IconTrendUp />}
            sub={averageScore != null ? t("Avg across 3 criteria", "متوسط 3 معايير") : t("Run assessment", "قم بالتقييم")}
          />
        </div>
      </div>

      {/* ── Charts row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title={t("Maturity Radar", "رادار النضج")}>
          {/* direction:ltr keeps chart axis labels readable in RTL mode */}
          <div style={{ direction: "ltr" }}>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={maturityRadarData}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="domain" tick={{ fontSize: 9, fill: "#576E8A" }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} />
                <Tooltip formatter={(value) => [`${value}%`]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Radar name={t("Current", "الحالي")} dataKey="current" stroke="#4682B4" fill="#4682B4" fillOpacity={0.25} />
                <Radar name={t("Target", "الهدف")} dataKey="target" stroke="#03C026" fill="#03C026" fillOpacity={0.1} strokeDasharray="4 3" />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title={t("Compliance Radar", "رادار الامتثال")}>
          <div style={{ direction: "ltr" }}>
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={complianceRadarData}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="domain" tick={{ fontSize: 9, fill: "#576E8A" }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9 }} />
                <Tooltip formatter={(value) => [`${value}%`]} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Radar name={t("Current", "الحالي")} dataKey="current" stroke="#FF8C00" fill="#FF8C00" fillOpacity={0.25} />
                <Radar name={t("Target", "الهدف")} dataKey="target" stroke="#03C026" fill="#03C026" fillOpacity={0.1} strokeDasharray="4 3" />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      {/* ── Ordered domains list ── */}
      <SectionCard title={t("NDI Domains", "مجالات مؤشر البيانات")}>
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 list-decimal ps-5">
          {ORDERED_DOMAINS.map((domain) => (
            <li key={domain.en} className="text-sm" style={{ color: "var(--text-heading)" }}>{t(domain.en, domain.ar)}</li>
          ))}
        </ol>
      </SectionCard>

      {/* ── Domain table ── */}
      <SectionCard title="" variant="table" hideTitle>
        <div className="overflow-x-auto -mx-4 -mb-4">
          <table className="min-w-full">
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {[
                  t("Domain Name", "اسم المجال"),
                  t("Maturity Score", "درجة النضج"),
                  t("Compliance Score", "درجة الامتثال"),
                ].map((h) => (
                  <th key={h} className="px-4 py-3 text-start font-medium uppercase tracking-wider" style={{ fontSize: "14px", color: "#9CA7B7" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: "var(--card-border)" }}>
              {ORDERED_DOMAINS.map((domain) => (
                <DomainRow
                  key={domain.en}
                  domain={t(domain.en, domain.ar)}
                  maturityScore={domain.en === "Data Classification" && maturityLevel != null ? `L${maturityLevel}` : "—"}
                  complianceScore={domain.en === "Data Classification" ? (dcCompliance > 0 ? `${dcCompliance}%` : "—") : "—"}
                  isLive={assessment != null && domain.en === "Data Classification"}
                  liveLabel={t("Live", "مباشر")}
                />
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
